import React, { useContext, useEffect, useState } from "react";
import styles from '../../styles/cr.module.scss';
import { CrDropdown } from "../cr/CrDropdown";
import { FormButtons } from "../cr/FormButtons";
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { MessageBar, MessageBarType } from '@fluentui/react';
import { DataContext } from "../DataContext";
import { CrLoadingOverlay } from "../cr/CrLoadingOverlay";
import useMobileDetector from "../useMobileDetector";

interface HeatMapConfigProps {
    onHeatMapConfigSaved?: () => Promise<void> | void;
}

export const HeatMapConfig = (props: HeatMapConfigProps): React.ReactElement => {
    const { dataServices: { riskAppBandService, riskAppScoreFormulaService } } = useContext(DataContext);

    const [loading, setLoading] = useState<boolean>(false);
    const [selectedBandId, setSelectedBandId] = useState<number>();
    const [riskBandOptions, setRiskBandOptions] = useState<IDropdownOption[]>([]);
    const [bandConfigs, setBandConfigs] = useState<any[]>([]);

    const [selectedFormulaId, setSelectedFormulaId] = useState<number>();
    const [formulaOptions, setFormulaOptions] = useState<IDropdownOption[]>([]);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");

    const isMobile = useMobileDetector();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async (): Promise<void> => {
        try {
            setLoading(true);
            const bandData = await riskAppBandService.readAll('?$orderby=NumBands');
            const formulaData = await riskAppScoreFormulaService.readAll('?$orderby=ID');

            console.log('Loaded heatmap configuration:', bandData);
            console.log('Loaded score formulas:', formulaData);

            setBandConfigs(bandData);

            setRiskBandOptions(
                bandData.map(item => ({
                    key: item.ID,
                    text: `${item.NumBands} Bands : ${item.Title}`
                }))
            );

            const selectedBand = bandData.find(item => item.SelectedBand);
            if (selectedBand) {
                setSelectedBandId(selectedBand.ID);
            }

            setFormulaOptions(
                formulaData.map(item => ({
                    key: item.ID,
                    text: `${item.Title} ${item.ScoreFormula}`
                }))
            );

            const selectedFormula = formulaData.find(item => item.SelectedFormula);
            if (selectedFormula) {
                setSelectedFormulaId(selectedFormula.ID);
            }
        }
        catch (err) {
            console.error('Failed to load heatmap configuration', err);
        }
        finally {
            setLoading(false);
        }
    };

    const handleSaveChanges = async (): Promise<void> => {
        try {
            setSuccessMessage("");
            setErrorMessage("");

            await riskAppBandService.updateHeatMapConfig({
                RiskAppBandID: selectedBandId,
                RiskScoreFormulaID: selectedFormulaId
            });

            setSuccessMessage("Heatmap configuration saved successfully.");
            setErrorMessage("");

            // reload local config and refresh parent data
            await loadData();
            if (props.onHeatMapConfigSaved) {
                await props.onHeatMapConfigSaved();
            }

        } catch (err) {
            console.error('Failed to save heat map configuration', err);
            setSuccessMessage("");
            setErrorMessage("Failed to save heatmap configuration.");
        }
    };

    const selectedConfig = bandConfigs.find(x => x.ID === selectedBandId);

    const previewBands = selectedConfig
        ? Array.from({ length: selectedConfig.NumBands }, (_, i) => ({
            title: selectedConfig[`TitleBand${i + 1}`],
            color: selectedConfig[`ColorBand${i + 1}`]
        }))
        : [];

    return (
        <div className={`${styles.cr}`}>
            <h2 style={{ flexGrow: 1 }} className={styles.listTitle}>
                Heatmap Configuration
            </h2>

            {loading ? (
                <CrLoadingOverlay
                    isLoading={true}
                    opaque={false}
                    style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}
                />
            ) : (
                <>
                    {successMessage && (
                        <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
                            {successMessage}
                        </MessageBar>
                    )}

                    {errorMessage && (
                        <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
                            {errorMessage}
                        </MessageBar>
                    )}

                    <div style={{
                        maxWidth: '600px',
                        marginRight: '10px',
                        marginLeft: '10px',
                        marginTop: '30px'
                    }}>
                        <CrDropdown
                            label="Number of Risk Bands"
                            options={riskBandOptions}
                            selectedKey={selectedBandId}
                            onChange={(_, option) => setSelectedBandId(option?.key as number)}
                            className={styles.formField}
                            required={true}
                        />

                        {selectedConfig &&
                            <div
                                style={{
                                    background: '#E6E7E0',
                                    padding: '8px',
                                    marginBottom: '20px',
                                    marginTop: '-10px',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '8px',
                                        marginBottom: '8px'
                                    }}
                                >
                                    {previewBands.map((band, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                flex: 1,
                                                height: '12px',
                                                backgroundColor: band.color
                                            }}
                                        />
                                    ))}
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '8px'
                                    }}
                                >
                                    {previewBands.map((band, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                flex: 1,
                                                fontSize: isMobile ? '11px' : '14px'
                                            }}
                                        >
                                            {index + 1}. {band.title}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        }

                        <CrDropdown
                            label="Risk Score Formula"
                            options={formulaOptions}
                            selectedKey={selectedFormulaId}
                            onChange={(_, option) => setSelectedFormulaId(option?.key as number)}
                            className={styles.formField}
                            required={true}
                        />

                        <FormButtons
                            primaryText="Save Changes"
                            onPrimaryClick={handleSaveChanges}
                            primaryDisabled={selectedBandId == null || selectedFormulaId == null}
                        />
                    </div>
                </>
            )}
        </div>
    );
};